/**
 * @tyndo/ui-kit/domain/CrossModuleCommandLauncher
 *
 * Closes G2.2 from docs/latest-analysis.md.
 *
 * Lets a record detail in one module trigger commands in other
 * modules without page-switching. Example: a Quality NCR detail
 * surfaces a "Flag supplier-quality" command that targets
 * Procurement; a Maintenance work-order detail surfaces a
 * "Issue spare from inventory" command that targets Inventory.
 *
 * Each command has an optional permission gate, an optional
 * e-signature gate (regulated workflows), and a confirmation copy
 * shown in a modal before the mutation fires.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * <CrossModuleCommandLauncher
 *   commands={[
 *     {
 *       id: 'flag-supplier',
 *       label: 'Flag supplier-quality',
 *       targetModule: 'procurement',
 *       icon: Flag,
 *       requiresPermission: 'procurement.write',
 *       requiresESignature: false,
 *       confirmation: 'Open a supplier-quality alert for ACME-001?',
 *       onCommit: async () => { ... await mutate(...) },
 *     },
 *   ]}
 * />
 * ```
 */

import * as React from 'react';
import { ArrowRightLeft, Lock } from 'lucide-react';
import { Button } from '../primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../primitives/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu';
import { Input } from '../primitives/input';
import { Label } from '../primitives/label';

export interface CrossModuleCommand {
  /** Stable id used for analytics + permission lookup. */
  id: string;
  /** Visible label in the launcher menu. */
  label: string;
  /** Target module (used for icon coloring + analytics). */
  targetModule: string;
  /** Optional Lucide icon component. */
  icon?: React.ComponentType<{ size?: number }>;
  /** Optional permission requirement; the caller's `usePermission` decides. */
  requiresPermission?: string;
  /** When true, the confirmation modal asks for an e-signature reason. */
  requiresESignature?: boolean;
  /** Confirmation prompt shown in the modal before commit. */
  confirmation: string;
  /** Disabled flag — e.g. permission absent. Tooltip-friendly. */
  disabled?: boolean;
  /** Reason a disabled command is grayed out — surfaced in tooltip + modal. */
  disabledReason?: string;
  /**
   * Commit handler. Resolves on success, throws on failure. The launcher
   * surfaces the throw via toast/error UI provided by the host.
   */
  onCommit: (signaturePayload?: { reason: string; signedBy: string }) => Promise<void>;
}

export interface CrossModuleCommandLauncherProps {
  /** Available commands. Empty array hides the launcher entirely. */
  commands: CrossModuleCommand[];
  /** Optional trigger label override. Defaults to "Cross-module actions". */
  triggerLabel?: string;
  /** Disable the whole launcher (e.g. record archived). */
  disabled?: boolean;
}

interface PendingCommand {
  command: CrossModuleCommand;
}

export const CrossModuleCommandLauncher: React.FC<CrossModuleCommandLauncherProps> = ({
  commands,
  triggerLabel = 'Cross-module actions',
  disabled = false,
}) => {
  const [pending, setPending] = React.useState<PendingCommand | null>(null);
  const [reason, setReason] = React.useState('');
  const [signedBy, setSignedBy] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (commands.length === 0) return null;

  const closeModal = () => {
    setPending(null);
    setReason('');
    setSignedBy('');
    setSubmitting(false);
    setError(null);
  };

  const onConfirm = async () => {
    if (!pending) return;
    setSubmitting(true);
    setError(null);
    try {
      const sigPayload =
        pending.command.requiresESignature && reason.trim() && signedBy.trim()
          ? { reason: reason.trim(), signedBy: signedBy.trim() }
          : undefined;
      await pending.command.onCommit(sigPayload);
      closeModal();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSubmitting(false);
    }
  };

  const sigValid = pending?.command.requiresESignature
    ? reason.trim().length > 0 && signedBy.trim().length > 0
    : true;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="flex items-center gap-2"
          >
            <ArrowRightLeft size={14} />
            {triggerLabel}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[260px]">
          <DropdownMenuLabel>Cross-module actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {commands.map(cmd => {
            const Icon = cmd.icon;
            return (
              <DropdownMenuItem
                key={cmd.id}
                disabled={cmd.disabled}
                onClick={e => {
                  e.preventDefault();
                  if (!cmd.disabled) setPending({ command: cmd });
                }}
                className="flex items-center gap-2"
                title={cmd.disabled ? cmd.disabledReason : undefined}
              >
                {Icon ? <Icon size={14} /> : null}
                <span className="flex-1">{cmd.label}</span>
                <span className="t-eyebrow text-muted-foreground">→ {cmd.targetModule}</span>
                {cmd.requiresESignature ? <Lock size={12} className="text-muted-foreground" /> : null}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={pending !== null} onOpenChange={open => !open && closeModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pending?.command.label}</DialogTitle>
            <DialogDescription>{pending?.command.confirmation}</DialogDescription>
          </DialogHeader>

          {pending?.command.requiresESignature && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="cmd-reason">Reason (e-signature)</Label>
                <Input
                  id="cmd-reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Why this command?"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cmd-signed-by">Signed by</Label>
                <Input
                  id="cmd-signed-by"
                  value={signedBy}
                  onChange={e => setSignedBy(e.target.value)}
                  placeholder="Operator id"
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              disabled={submitting || !sigValid}
            >
              {submitting ? 'Committing…' : 'Commit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
